import React, { useState } from 'react';
import styled from 'styled-components';

import { Modal, Button, LoadingButton } from '@common/index';
import { Alert, Stack } from '@mui/material';

import { useDispatch, useSelector } from 'react-redux';
import { flowActions, tabActions } from '@store/slices';
import { AppDispatch, State } from '@store/index';
import { CodeOutlined, PlayArrowOutlined } from '@mui/icons-material';
import { Label } from '@common/Label';
import { BasicInput } from '@common/Expression/Basic';
import { TranslationNode } from '@components/Flow';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { executeNode } from '@requests/nodes';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { useTranslate } from '@common/hooks/useTranslate';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  gap: 1rem;
  padding-bottom: 0.5rem;
`;

const NodeContainer = styled.div``;

interface NodeProps {
  variable: string;
  variableValues: { [key: string]: string };
  setVariableValues: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
}

const NodeTestRunVariable: React.FC<NodeProps> = ({ variableValues, setVariableValues, variable }) => {
  return (
    <NodeContainer>
      <Stack spacing={1}>
        <Label
          title={variable}
          tooltip='This dynamic variable was used in an input field for this node. You need to provide a value for this variable to execute it without running the entire workflow.'
        />
        <BasicInput
          key={variable}
          keyProp={variable}
          value={variableValues[variable]}
          setValue={(newValue: string) => setVariableValues((prev) => ({ ...prev, [variable]: newValue }))}
          placeholder='Value'
          minRows={1}
          maxRows={12}
        />
      </Stack>
    </NodeContainer>
  );
};

interface Props {
  setTab: React.Dispatch<string>;
}

export const NodeTestRun: React.FC<Props> = ({ setTab }) => {
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();

  const { translate } = useTranslate();
  const { service_id, workflow_id } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const commands = useSelector((state: State) => state.tabs.active.commands);
  const completionNodes = commands.filter((command) => command.config.command_id === 'llm.completion');
  const isMessageArray = completionNodes.some((node) => node.state?.message_array?.enabled);

  const authentication = useSelector((state: State) => state.tabs.active.flow?.authentication);

  const testRun = useSelector((state: State) => state.flow.nodeTestRun);
  const nodeId = testRun?.node_id || '';
  const setOpen = (val: TranslationNode | null) => dispatch(flowActions.setNodeTestRun(val));

  const [variableValues, setVariableValues] = useState<{ [key: string]: string }>({});

  const { mutateAsync: execute, isPending: loading } = useMutation({
    mutationFn: executeNode,
    onMutate: () => {
      dispatch(tabActions.setNodeLoading(nodeId));
      closeModal();
    },
    onSuccess: (data) => {
      if (data?.error) {
        dispatch(tabActions.stopNodeLoading(data?.node_id));

        enqueueSnackbar('Failed to execute node.', {
          variant: 'error',
          autoHideDuration: 10000,
        });

        enqueueSnackbar(data?.error?.error || data?.error, {
          variant: 'error',
          autoHideDuration: 10000,
        });
        return;
      }

      enqueueSnackbar('Node executed successfully.', {
        variant: 'success',
        autoHideDuration: 3000,
      });

      queryClient.invalidateQueries({ queryKey: ['workflows'] });

      const { node_id, ...dataWithoutNodeId } = data;
      dispatch(tabActions.refreshDynamicNodeData({ node_id, output: dataWithoutNodeId }));
      dispatch(tabActions.stopNodeLoading(node_id));
      setTab('3');
    },
  });

  const closeModal = () => {
    setVariableValues({});
    setOpen(null);
  };

  const runNode = () => {
    if (testRun) {
      const translation = translate(workflow_id as string);
      const edges = translation.edges.filter((edge) => edge?.target === testRun.node_id && edge.variant === 'plugin');
      const plugins = edges.map((edge) => translation.nodes.find((node) => node.node_id === edge.source));

      const node = { ...testRun, testData: variableValues };
      execute({ service_id, node, plugins, edges, authentication });
    }
  };

  const isAllVariablesFilled =
    Object.entries(variableValues).length === testRun?.variables.length &&
    Object.values(variableValues).every((val) => val.length > 0);

  return (
    <Modal
      open={Boolean(testRun)}
      background='black'
      setOpen={closeModal}
      header='Run Node'
      info={`This node has dynamic variables that need to given a value before it can be executed.`}
      icon={<CodeOutlined />}
      body={
        <Container>
          {isMessageArray && (
            <Alert severity='warning'>
              {`The run node feature is unsupported when the message array is defined as an expression. If you would like to test this node, hover the "Messages" input box and change the type to "Fixed" to test this node.`}
            </Alert>
          )}
          {!isMessageArray &&
            testRun?.variables.map((variable) => (
              <NodeTestRunVariable
                key={variable}
                variable={variable}
                variableValues={variableValues}
                setVariableValues={setVariableValues}
              />
            ))}
        </Container>
      }
      actions={
        <Stack direction='row' spacing={1}>
          <Button small onClick={closeModal}>
            Close
          </Button>
          <LoadingButton
            onClick={runNode}
            loading={loading}
            minWidth='100px'
            small
            customColor='primary'
            startIcon={<PlayArrowOutlined />}
            disabled={!isAllVariablesFilled}
          >
            Execute
          </LoadingButton>
        </Stack>
      }
    />
  );
};
